import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import { copy } from "../content/copy";
import { isWindowOpen } from "./window";
import type { HallWindow } from "./window";

// The Lab's spine: every experience registers here, lazy-loads its own chunk,
// and can be feature-flagged off without touching any other file.
export type Experience = {
  id: string;
  path: string;
  title: string;
  blurb: string;
  badge: string;
  enabled: boolean;
  /** Season events: outside this window the hall is closed everywhere. */
  window?: HallWindow;
  Component: LazyExoticComponent<ComponentType>;
};

export const experiences: Experience[] = [
  {
    id: "daily",
    path: "/lab/daily",
    title: copy.lab.items.daily.title,
    blurb: copy.lab.items.daily.blurb,
    badge: copy.lab.items.daily.badge,
    enabled: true,
    Component: lazy(() => import("./daily/DailyPop")),
  },
  {
    id: "musicbox",
    path: "/lab/musicbox",
    title: copy.lab.items.musicbox.title,
    blurb: copy.lab.items.musicbox.blurb,
    badge: copy.lab.items.musicbox.badge,
    enabled: true,
    Component: lazy(() => import("./musicbox/MusicBox")),
  },
  {
    id: "bellows",
    path: "/lab/bellows",
    title: copy.lab.items.bellows.title,
    blurb: copy.lab.items.bellows.blurb,
    badge: copy.lab.items.bellows.badge,
    enabled: true,
    Component: lazy(() => import("./bellows/Bellows")),
  },
  {
    id: "observatory",
    path: "/lab/observatory",
    title: copy.lab.items.observatory.title,
    blurb: copy.lab.items.observatory.blurb,
    badge: copy.lab.items.observatory.badge,
    enabled: true,
    Component: lazy(() => import("./observatory/Observatory")),
  },
  {
    id: "soundhall",
    path: "/lab/soundhall",
    title: copy.lab.items.soundhall.title,
    blurb: copy.lab.items.soundhall.blurb,
    badge: copy.lab.items.soundhall.badge,
    enabled: true,
    Component: lazy(() => import("./soundhall/SoundHall")),
  },
  {
    id: "dreamlab",
    path: "/lab/dreamlab",
    title: copy.lab.items.dreamlab.title,
    blurb: copy.lab.items.dreamlab.blurb,
    badge: copy.lab.items.dreamlab.badge,
    enabled: true,
    Component: lazy(() => import("./dreamlab/DreamLab")),
  },
  {
    id: "gallery",
    path: "/lab/gallery",
    title: copy.lab.items.gallery.title,
    blurb: copy.lab.items.gallery.blurb,
    badge: copy.lab.items.gallery.badge,
    enabled: true,
    Component: lazy(() => import("./gallery/Gallery")),
  },
  {
    id: "cinema",
    path: "/lab/cinema",
    title: copy.lab.items.cinema.title,
    blurb: copy.lab.items.cinema.blurb,
    badge: copy.lab.items.cinema.badge,
    enabled: true,
    Component: lazy(() => import("./cinema/Cinema")),
  },
];

// Home's card grid and the router both go through these, so a hall outside
// its window vanishes from the map and 404s at its route in the same breath.
export function openExperiences(now: Date = new Date()): Experience[] {
  return experiences.filter((e) => e.enabled && isWindowOpen(e.window, now));
}

export function findExperience(path: string, now: Date = new Date()): Experience | undefined {
  return openExperiences(now).find((e) => e.path === path);
}
